'use client';

import { c as _c } from "react/compiler-runtime";
import { applySiteMigration, lookupSiteMigration, type ApplySiteMigrationResult, type LookupSiteMigrationResult, type ResolvedSite, type SiteImportRow } from '@/app/_workflows/migrateSiteData';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Typography from '@mui/material/Typography';
import { Modal } from '@shared/components/Modal';
import { useServerHandler } from '@shared/hooks/useServerHandler';
import { useCallback, useRef, useState } from 'react';
import * as XLSX from 'xlsx';
interface MigrateSiteDataModalProps {
  isOpen: boolean;
  onClose(): void;
}
const SITE_COL = 'Site';
const COMPANY_COL = 'Company';
const ADDRESS_COL = 'Address';
const CITY_COL = 'City';
const STATE_COL = 'State';
const ZIP_COL = 'Zip';
const COUNTRY_COL = 'Country';
const SITE_MANAGER_COL = 'Site Manager';
const JMT_COL = 'JMT';
const QMT_COL = 'QMT';
const IS_ACTIVE_COL = 'isActive';
const SALES_ENGINEER_NAME_COL = 'Sales Engineer Name';
const SALES_ENGINEER_EMAIL_COL = 'Sales Engineer Email';
type Step = 'upload' | 'review' | 'done';
type AccessChoice = 'skip' | 'jmt' | 'qmt';
function parseBoolean(value: unknown): boolean {
  if (typeof value === 'boolean') return value;
  return String(value ?? '').trim().toLowerCase() === 'true';
}
function parseFile(file: File): Promise<SiteImportRow[]> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = e => {
      try {
        const data = new Uint8Array(e.target!.result as ArrayBuffer);
        const workbook = XLSX.read(data, {
          type: 'array'
        });
        const sheet = workbook.Sheets[workbook.SheetNames[0]];
        const rows = XLSX.utils.sheet_to_json<Record<string, unknown>>(sheet, {
          defval: null
        });
        const parsed: SiteImportRow[] = rows.map(row => ({
          site: String(row[SITE_COL] ?? '').trim(),
          company: String(row[COMPANY_COL] ?? '').trim(),
          address: String(row[ADDRESS_COL] ?? '').trim(),
          city: String(row[CITY_COL] ?? '').trim(),
          state: String(row[STATE_COL] ?? '').trim(),
          zip: String(row[ZIP_COL] ?? '').trim(),
          country: String(row[COUNTRY_COL] ?? '').trim(),
          siteManager: String(row[SITE_MANAGER_COL] ?? '').trim(),
          jmtEnabled: parseBoolean(row[JMT_COL]),
          qmtEnabled: parseBoolean(row[QMT_COL]),
          isActive: parseBoolean(row[IS_ACTIVE_COL]),
          salesEngineerName: String(row[SALES_ENGINEER_NAME_COL] ?? '').trim(),
          salesEngineerEmail: String(row[SALES_ENGINEER_EMAIL_COL] ?? '').trim()
        })).filter(r => r.site !== '' && r.address !== '');
        resolve(parsed);
      } catch (err) {
        reject(err);
      }
    };
    reader.onerror = () => reject(reader.error);
    reader.readAsArrayBuffer(file);
  });
}
export const MigrateSiteDataModal: React.FC<MigrateSiteDataModalProps> = t0 => {
  const $ = _c(56);
  const {
    isOpen,
    onClose
  } = t0;
  const [step, setStep] = useState("upload");
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = [];
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const [rows, setRows] = useState(t1);
  const [fileName, setFileName] = useState(null);
  const [parseError, setParseError] = useState(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [lookup, setLookup] = useState(null);
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {};
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const [accessChoices, setAccessChoices] = useState(t2);
  const [applyResult, setApplyResult] = useState(null);
  const fileInputRef = useRef(null);
  const {
    isLoading,
    doServerAction
  } = useServerHandler();
  let t3;
  if ($[2] !== isLoading || $[3] !== onClose) {
    t3 = () => {
      if (isLoading) {
        return;
      }
      setStep("upload");
      setRows([]);
      setFileName(null);
      setParseError(null);
      setLookup(null);
      setAccessChoices({});
      setApplyResult(null);
      onClose();
    };
    $[2] = isLoading;
    $[3] = onClose;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const handleClose = t3;
  let t4;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = async file => {
      setParseError(null);
      setRows([]);
      setFileName(file.name);
      try {
        const parsed = await parseFile(file);
        if (parsed.length === 0) {
          setParseError(`No valid rows found. File must have a "${SITE_COL}" column.`);
          return;
        }
        setRows(parsed);
      } catch {
        setParseError("Failed to parse file. Make sure it is a valid Excel or CSV file.");
      }
    };
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const processFile = t4;
  let t5;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = e => {
      e.preventDefault();
      setIsDragOver(false);
      const file_0 = e.dataTransfer.files[0];
      if (file_0) {
        processFile(file_0);
      }
    };
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  const handleDrop = t5;
  let t6;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = e_0 => {
      const file_1 = e_0.target.files?.[0];
      if (file_1) {
        processFile(file_1);
      }
      e_0.target.value = "";
    };
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  const handleFileChange = t6;
  let t7;
  if ($[8] !== lookup?.needsAccess) {
    t7 = lookup?.needsAccess ?? [];
    $[8] = lookup?.needsAccess;
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  let t8;
  if ($[10] !== accessChoices || $[11] !== t7) {
    let t9;
    if ($[13] !== accessChoices) {
      t9 = site => {
        const choice = accessChoices[site.name] ?? "skip";
        if (choice === "skip") {
          return [];
        }
        return [{
          ...site,
          data: {
            ...site.data,
            jmtEnabled: choice === "jmt",
            qmtEnabled: choice === "qmt"
          }
        }];
      };
      $[13] = accessChoices;
      $[14] = t9;
    } else {
      t9 = $[14];
    }
    t8 = t7.flatMap(t9);
    $[10] = accessChoices;
    $[11] = t7;
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  const includedNeedsAccess = t8;
  const totalResolved = lookup ? lookup.toUpdate.length + lookup.toCreate.length + includedNeedsAccess.length : 0;
  let t9;
  if ($[15] !== doServerAction || $[16] !== rows) {
    t9 = () => {
      doServerAction(async () => ({
        data: await lookupSiteMigration(rows)
      }), result => {
        if (!result || typeof result !== "object" || !("data" in result)) {
          return;
        }
        setLookup(result.data);
        setAccessChoices({});
        setStep("review");
      });
    };
    $[15] = doServerAction;
    $[16] = rows;
    $[17] = t9;
  } else {
    t9 = $[17];
  }
  const handleLookup = t9;
  let t10;
  if ($[18] !== doServerAction || $[19] !== includedNeedsAccess || $[20] !== lookup) {
    t10 = () => {
      if (!lookup) {
        return;
      }
      doServerAction(async () => ({
        data: await applySiteMigration([...lookup.toUpdate, ...lookup.toCreate, ...includedNeedsAccess])
      }), result_0 => {
        if (!result_0 || typeof result_0 !== "object" || !("data" in result_0)) {
          return;
        }
        setApplyResult(result_0.data);
        setStep("done");
      });
    };
    $[18] = doServerAction;
    $[19] = includedNeedsAccess;
    $[20] = lookup;
    $[21] = t10;
  } else {
    t10 = $[21];
  }
  const handleConfirm = t10;
  let t11;
  if ($[22] !== handleClose || $[23] !== handleConfirm || $[24] !== handleLookup || $[25] !== isLoading || $[26] !== lookup || $[27] !== rows || $[28] !== step || $[29] !== totalResolved) {
    t11 = () => {
      if (step === "upload") {
        return <Button variant="contained" disabled={rows.length === 0 || isLoading} onClick={handleLookup}>Next {rows.length > 0 ? `(${rows.length} rows)` : ""}</Button>;
      }
      if (step === "review") {
        return <Box sx={{
          display: "flex",
          gap: 1
        }}><Button variant="outlined" disabled={isLoading} onClick={() => setStep("upload")}>Back</Button><Button variant="contained" disabled={!lookup || totalResolved === 0 || isLoading} onClick={handleConfirm}>{"Confirm & Save "}{lookup ? `(${totalResolved})` : ""}</Button></Box>;
      }
      return <Button variant="contained" onClick={handleClose}>Close</Button>;
    };
    $[22] = handleClose;
    $[23] = handleConfirm;
    $[24] = handleLookup;
    $[25] = isLoading;
    $[26] = lookup;
    $[27] = rows;
    $[28] = step;
    $[29] = totalResolved;
    $[30] = t11;
  } else {
    t11 = $[30];
  }
  const renderActions = t11;
  let t12;
  if ($[31] !== renderActions) {
    t12 = renderActions();
    $[31] = renderActions;
    $[32] = t12;
  } else {
    t12 = $[32];
  }
  let t13;
  if ($[33] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = {};
    $[33] = t13;
  } else {
    t13 = $[33];
  }
  let t14;
  if ($[34] !== fileName || $[35] !== isDragOver || $[36] !== parseError || $[37] !== rows || $[38] !== step) {
    t14 = step === "upload" && <><Box onDrop={handleDrop} onDragOver={e_1 => {
        e_1.preventDefault();
        setIsDragOver(true);
      }} onDragLeave={() => setIsDragOver(false)} onClick={() => fileInputRef.current?.click()} sx={{
        mt: 1,
        mb: 2,
        border: 2,
        borderStyle: "dashed",
        borderColor: isDragOver ? "primary.main" : "divider",
        borderRadius: 2,
        p: 4,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 1,
        cursor: "pointer",
        backgroundColor: isDragOver ? "action.hover" : "background.paper",
        transition: "all 0.15s ease"
      }}><CloudUploadIcon sx={{
          fontSize: 40,
          color: "text.secondary"
        }} /><Typography variant="body2" color="text.secondary">{"Drag & drop or click to select a file"}</Typography><Typography variant="caption" color="text.disabled">Supports .xlsx, .xls, .csv</Typography><input ref={fileInputRef} type="file" accept=".xlsx,.xls,.csv" style={{
          display: "none"
        }} onChange={handleFileChange} /></Box>{parseError && <Alert severity="error" sx={{
        mb: 2
      }}>{parseError}</Alert>}{rows.length > 0 && <Box sx={{
        display: "flex",
        alignItems: "center",
        gap: 1,
        flexWrap: "wrap"
      }}><Chip label={`${rows.length} rows ready`} color="success" size="small" />{fileName && <Typography variant="caption" color="text.secondary">{fileName}</Typography>}</Box>}</>;
    $[34] = fileName;
    $[35] = isDragOver;
    $[36] = parseError;
    $[37] = rows;
    $[38] = step;
    $[39] = t14;
  } else {
    t14 = $[39];
  }
  let t15;
  if ($[40] !== accessChoices || $[41] !== includedNeedsAccess || $[42] !== lookup || $[43] !== step) {
    t15 = step === "review" && lookup && <Box sx={{
      display: "flex",
      flexDirection: "column",
      gap: 2
    }}><Alert severity={lookup.ambiguous.length > 0 || lookup.needsAccess.length > 0 ? "warning" : "success"}>{lookup.toUpdate.length} to update, {lookup.toCreate.length} to create{includedNeedsAccess.length > 0 ? ` (incl. ${includedNeedsAccess.length} enabled below)` : ""}{lookup.ambiguous.length > 0 ? `, ${lookup.ambiguous.length} skipped (ambiguous)` : ""}. Review and confirm to save.</Alert>{lookup.toUpdate.length > 0 && <Box><Typography variant="subtitle2" color="success.main" gutterBottom={true}>Update ({lookup.toUpdate.length})</Typography><Divider sx={{
          mb: 1
        }} /><List dense={true} disablePadding={true}>{lookup.toUpdate.map(_temp)}</List></Box>}{lookup.toCreate.length > 0 && <Box><Typography variant="subtitle2" color="info.main" gutterBottom={true}>Create ({lookup.toCreate.length})</Typography><Divider sx={{
          mb: 1
        }} /><List dense={true} disablePadding={true}>{lookup.toCreate.map(_temp2)}</List></Box>}{lookup.ambiguous.length > 0 && <Box><Typography variant="subtitle2" color="warning.main" gutterBottom={true}>Skipped — ambiguous name ({lookup.ambiguous.length})</Typography><Divider sx={{
          mb: 1
        }} /><List dense={true} disablePadding={true}>{lookup.ambiguous.map(_temp3)}</List></Box>}{lookup.needsAccess.length > 0 && <Box><Typography variant="subtitle2" color="warning.main" gutterBottom={true}>{"Both JMT & QMT disabled ("}{lookup.needsAccess.length})</Typography><Typography variant="caption" color="text.secondary" sx={{
          display: "block",
          mb: 1
        }}>A site must have at least one program enabled. Choose one to include it, or leave it skipped.</Typography><Divider sx={{
          mb: 1
        }} /><List dense={true} disablePadding={true}>{lookup.needsAccess.map(s_1 => <ListItem key={`n-${s_1.name}`} disableGutters={true} sx={{
            flexDirection: "column",
            alignItems: "flex-start",
            gap: 0.5
          }}><ListItemText primary={`${s_1.name} — ${s_1.data.companyName ?? "No company"}`} secondary={`${s_1.data.city}, ${s_1.data.country} • ${s_1.action === "update" ? "existing site" : "new site"}`} primaryTypographyProps={{
              variant: "body2"
            }} secondaryTypographyProps={{
              variant: "caption",
              color: "text.secondary"
            }} /><ToggleButtonGroup size="small" exclusive={true} value={accessChoices[s_1.name] ?? "skip"} onChange={(_, value) => {
              if (value) {
                setAccessChoices(prev => ({
                  ...prev,
                  [s_1.name]: value
                }));
              }
            }}><ToggleButton value="skip">Skip</ToggleButton><ToggleButton value="jmt">Enable JMT</ToggleButton><ToggleButton value="qmt">Enable QMT</ToggleButton></ToggleButtonGroup></ListItem>)}</List></Box>}</Box>;
    $[40] = accessChoices;
    $[41] = includedNeedsAccess;
    $[42] = lookup;
    $[43] = step;
    $[44] = t15;
  } else {
    t15 = $[44];
  }
  let t16;
  if ($[45] !== applyResult || $[46] !== step) {
    t16 = step === "done" && applyResult && <Box sx={{
      display: "flex",
      flexDirection: "column",
      gap: 2
    }}><Box sx={{
        display: "flex",
        alignItems: "center",
        gap: 1
      }}><CheckCircleOutlineIcon color={applyResult.errors.length > 0 ? "warning" : "success"} /><Typography variant="body1" fontWeight={500}>Migration complete</Typography></Box><Alert severity={applyResult.errors.length > 0 ? "warning" : "success"}>{applyResult.updated} site{applyResult.updated !== 1 ? "s" : ""} updated, {applyResult.created} created successfully.</Alert>{applyResult.errors.length > 0 && <Box><Typography variant="subtitle2" color="error.main" gutterBottom={true}>Errors ({applyResult.errors.length})</Typography><Divider sx={{
          mb: 1
        }} /><List dense={true} disablePadding={true}>{applyResult.errors.map(_temp4)}</List></Box>}</Box>;
    $[45] = applyResult;
    $[46] = step;
    $[47] = t16;
  } else {
    t16 = $[47];
  }
  let t17;
  if ($[48] !== handleClose || $[49] !== isLoading || $[50] !== isOpen || $[51] !== t12 || $[52] !== t14 || $[53] !== t15 || $[54] !== t16) {
    t17 = <Modal isOpen={isOpen} onClose={handleClose} title="Migrate site data from excel" subTitle="Upload an Excel or CSV file with the site columns. Sites are matched by name; the site manager is matched by user name." maxWidth="sm" fullWidth={true} disableClose={isLoading} isLoading={isLoading} actions={t12} ContentProps={t13}>{t14}{t15}{t16}</Modal>;
    $[48] = handleClose;
    $[49] = isLoading;
    $[50] = isOpen;
    $[51] = t12;
    $[52] = t14;
    $[53] = t15;
    $[54] = t16;
    $[55] = t17;
  } else {
    t17 = $[55];
  }
  return t17;
};
function _temp(s) {
  return <ListItem key={`u-${s.id}`} disableGutters={true}><ListItemText primary={`${s.name} — ${s.data.companyName ?? "No company"}`} secondary={s.unmatchedManager ? `${s.data.city}, ${s.data.country} — site manager "${s.unmatchedManager}" not matched` : `${s.data.city}, ${s.data.country}`} primaryTypographyProps={{
      variant: "body2"
    }} secondaryTypographyProps={{
      variant: "caption",
      color: s.unmatchedManager ? "warning.main" : "text.secondary"
    }} /></ListItem>;
}
function _temp2(s_0) {
  return <ListItem key={`c-${s_0.name}`} disableGutters={true}><ListItemText primary={`${s_0.name} — ${s_0.data.companyName ?? "No company"}`} secondary={s_0.unmatchedManager ? `${s_0.data.city}, ${s_0.data.country} — site manager "${s_0.unmatchedManager}" not matched` : `${s_0.data.city}, ${s_0.data.country}`} primaryTypographyProps={{
      variant: "body2"
    }} secondaryTypographyProps={{
      variant: "caption",
      color: s_0.unmatchedManager ? "warning.main" : "text.secondary"
    }} /></ListItem>;
}
function _temp3(name) {
  return <ListItem key={`a-${name}`} disableGutters={true}><ListItemText primary={name} primaryTypographyProps={{
      variant: "body2",
      color: "text.secondary"
    }} /></ListItem>;
}
function _temp4(name_0) {
  return <ListItem key={name_0} disableGutters={true}><ListItemText primary={name_0} primaryTypographyProps={{
      variant: "body2",
      color: "error"
    }} /></ListItem>;
}